/**
 * The Considered Home — full guide copy.
 * Voice: calm, sensory, generous, unhurried. Merilyn Smith.
 */

export const GUIDE_TITLE = "The Sacred Home";
export const GUIDE_SUBTITLE = "Five quiet shifts that make a home feel finished.";
export const GUIDE_BYLINE = "By Merilyn Smith · Arc & Light";

export const GUIDE_PREFACE = {
    eyebrow: "A note to begin",
    title: "What \u201Cfinished\u201D really means.",
    body: [
        "A home rarely becomes finished by adding more. It becomes finished by alignment \u2014 by the moment its pieces stop competing and start belonging together.",
        "After fifteen years of reading rooms \u2014 for editorial, for fashion, for fine art, and now for the families I work with \u2014 I\u2019ve come to believe that the difference between a home that looks fine and one that feels finished is almost never about taste. It is almost always about attention.",
        "What follows are five small acts of attention. They cost nothing. They ask only that you slow down enough to see your home clearly. When you do, the home you already have is usually closer to finished than it looks.",
    ],
    signature: "Merilyn",
};

export const GUIDE_SHIFTS = [
    {
        n: "01",
        title: "Light, before everything else.",
        lede: "Most homes are decorated against the room\u2019s natural light. The finished ones are designed with it.",
        body: [
            "Walk through your home at three points of the day \u2014 early morning, late afternoon, the moment just before dusk. Notice which rooms gather light and which quietly lose it. The tones that already live in your light should set your palette, not the other way around.",
            "A north-facing room asks for warmer tones to soften the cool blue cast it receives. A south-facing room can hold cooler whites and stones without ever feeling clinical. East-facing rooms come alive at breakfast. West-facing rooms wait until late afternoon to show their best self.",
            "Once you know the room\u2019s light, every other decision narrows. The palette suggests itself. The materials suggest themselves. The room begins to design with you.",
        ],
        tryThis:
            "Spend one Saturday morning sitting in each room for ten minutes without touching anything. Write a single word for the light in that room \u2014 \u201Cgolden,\u201D \u201Csilver,\u201D \u201Cwashed,\u201D \u201Cdeep.\u201D Build the room around that word.",
    },
    {
        n: "02",
        title: "Choose one quiet thread.",
        lede: "Rooms feel disconnected when each one is decorated as its own project. A single thread, repeated softly, makes a house feel like one home.",
        body: [
            "It can be a material \u2014 linen at the windows, linen on the sofa, linen on the bed. It can be a tone \u2014 a warm clay that appears in a lamp base, a ceramic, a single line drawing on a wall. It can be a form \u2014 a soft curve repeated three times across three rooms.",
            "The thread doesn\u2019t need to dominate. It only needs to recur. The eye doesn\u2019t consciously notice it; it simply recognises that the home is one place, not five.",
            "Most homes I walk into already have a thread waiting to be honoured. Finding it is usually a matter of standing still long enough to see what your home is quietly trying to be.",
        ],
        tryThis:
            "Choose one tactile material that already lives somewhere in your home and that you love. Add it deliberately in two more rooms \u2014 not as decoration, but as continuity.",
    },
    {
        n: "03",
        title: "Subtract first. Then choose.",
        lede: "The most common mistake is buying more before removing what isn\u2019t quite right.",
        body: [
            "Before you decide what to add, remove anything in the room that doesn\u2019t earn its place. The chair you don\u2019t sit in. The frame that no longer speaks to you. The decorative object held by habit, not love. Aim for roughly thirty percent less.",
            "Live with the lighter room for a week. Sit in it at different times. Eat in it once or twice. Notice what the room asks for now that it has been given space to breathe.",
            "More often than you\u2019d expect, the room becomes what it was meant to be without a single new purchase. And the few things you do genuinely need become obvious \u2014 which means you spend with confidence, not hope.",
        ],
        tryThis:
            "Photograph each room from the doorway. In the photo, mark anything you\u2019d remove if you could. Spend a weekend removing exactly those things. Don\u2019t replace anything yet.",
    },
    {
        n: "04",
        title: "Anchor with weight. Protect the breath.",
        lede: "Every room needs one piece that holds it down. Without it, the room floats. With it, everything else can be light.",
        body: [
            "The anchor might be a generous sofa, a heavy timber table, a deep wool rug, a substantial bed with quiet bedding. It is the piece your eye returns to. The piece the room is built around.",
            "Once the anchor is in place, the second discipline begins: protecting the negative space around it. Leave the wall above the dining table empty a little longer than feels comfortable. Leave one shelf only half-styled. Let the floor near the sofa stay clear.",
            "The breath in a room is not absence \u2014 it is intention. It is what makes a calm room feel calm rather than busy. It is what allows the few considered things to be heard.",
        ],
        tryThis:
            "Identify the anchor in each room. If you can\u2019t name one, that is the next piece worth investing in \u2014 before another cushion, another vase, another small thing.",
    },
    {
        n: "05",
        title: "Texture is the feeling.",
        lede: "Colour gets the credit. Texture does the quiet work.",
        body: [
            "A linen the eye reads as creamy but the hand reads as cool. A clay vessel that catches light differently at every hour. A wool throw heavier than it looks. A heavy curtain that softens an entire room before you\u2019ve moved a single piece of furniture.",
            "The rooms that feel finished are almost always the rooms where every surface invites the hand. Smooth ceramic against rough linen. Cool stone against warm timber. Polished glass against matte plaster.",
            "When a room feels not-quite-right, the missing layer is almost never another colour. It is another way of being touched.",
        ],
        tryThis:
            "In the room you find most unfinished, list the surfaces in order from softest to hardest. If the list is short, that is the missing layer.",
    },
];

export const GUIDE_CLOSING = {
    title: "Five small acts of attention.",
    body: [
        "These shifts aren\u2019t a recipe \u2014 they\u2019re a way of slowing down enough to see your home clearly.",
        "Read the light. Find the thread. Subtract before you add. Anchor the room and protect its breath. Trust texture over colour.",
        "If, after all of this, your home still doesn\u2019t quite feel finished \u2014 that is the moment a trained eye in the room with you matters most. I\u2019d be honoured to begin that conversation.",
    ],
    sign: "Merilyn",
    role: "Founder, Arc & Light",
};
